import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { PRINCIPLES } from "@/data/content";

const IMG = "/images/mission-city.jpg";

export default function Mission() {
  return (
    <div data-testid="mission-page">
      {/* Intro */}
      <section className="pt-40 pb-20 bg-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-8">Our mission</SectionLabel>
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#F5F5F0] leading-[1.1]">
              We believe effective protection begins with{" "}
              <span className="text-[#C9A24B]">understanding.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-10 text-xl text-[#A1A1AA] font-light leading-relaxed">
              Blackthorne Stratton operates at the intersection of security and advisory services —
              offering clients more than physical protection alone. Through a proactive, intelligence-driven
              approach, we support individuals, businesses, legal professionals and organisations facing
              complex personal, operational and reputational risks.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Approach split */}
      <section className="py-20 bg-[#08080A] border-y border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="aspect-[5/6] overflow-hidden">
                <img src={IMG} alt="Discreet professional advisory" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
              </div>
              <div className="absolute -bottom-px -left-px border-l-2 border-b-2 border-[#C9A24B] w-24 h-24" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel className="mb-6">Our approach</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F5F0] leading-tight">
              Intelligence, preparation and discretion — rather than visible force.
            </h2>
            <p className="mt-8 text-[#A1A1AA] font-light leading-relaxed text-lg">
              Every client, environment and threat profile is different. Built on the principles of
              professionalism, discretion and strategic understanding, we combine operational security
              expertise with intelligence-led consultancy to provide tailored solutions across close
              protection, investigations, behavioural detection and risk management.
            </p>
            <p className="mt-5 text-[#A1A1AA] font-light leading-relaxed">
              Our personnel combine deep operational experience with a measured, professional consultancy
              mindset — ensuring the right response in every environment.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Core principles */}
      <section className="py-24 sm:py-32 bg-[#0A0A0B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-6">Core principles</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F5F5F0] max-w-2xl">
              The values that guide every engagement.
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 0.08}>
                <div className="h-full bg-[#121214] border border-[#27272A] p-8 lg:p-10 hover:border-[#C9A24B]/50 transition-colors duration-500">
                  <span className="font-serif text-3xl text-[#C9A24B]/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 font-serif text-2xl text-[#F5F5F0]">{p.title}</h3>
                  <p className="mt-3 text-[#A1A1AA] font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#08080A] border-t border-[#27272A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F5F0]">
              Understand · Advise · Protect
            </h2>
            <p className="mt-6 text-[#A1A1AA] font-light text-lg">
              Partner with a team that places understanding at the heart of protection.
            </p>
            <Link
              to="/contact"
              data-testid="mission-contact-cta"
              className="group mt-10 inline-flex items-center gap-2 bg-[#C9A24B] text-black px-10 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#E5E4E2] transition-colors duration-300"
            >
              Start a conversation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
